/**
 * C8 — the AI Summary widget's panel, as AgencyAnalytics': what to focus on,
 * Generate (SynAssistant reads this page's numbers on the server and writes a
 * few plain sentences), and the text itself, which the agency edits before a
 * client sees it. Clients only ever see the saved text.
 */

import { useState, type ReactNode } from 'react';

import { assistantApi } from '../assistant/assistant-api.ts';
import { queryOf, widgetQuery } from './hooks.ts';
import type {
  DocumentSettings,
  Page,
  ReportDocument,
  Widget,
  WidgetDisplay,
} from './reports-api.ts';

export function AiSummarySettings(props: {
  organisationId: string;
  document: ReportDocument;
  settings: DocumentSettings;
  page: Page;
  widget: Widget;
  onChange: (widget: Widget) => void;
  onClose: () => void;
}): ReactNode {
  const { widget } = props;
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const setDisplay = (patch: Partial<WidgetDisplay>) =>
    props.onChange({ ...widget, display: { ...widget.display, ...patch } });
  const generate = () => {
    const businessUnitId = props.document.business_unit_id;
    setBusy(true);
    setProblem(null);
    assistantApi
      .summary(props.organisationId, {
        business_unit_id: businessUnitId,
        ...(businessUnitId ? {} : { client_ids: props.settings.clients ?? [] }),
        ...queryOf(props.settings),
        title: props.page.title,
        guidance: widget.display.guidance ?? '',
        widgets: props.page.widgets
          .map((item, index) => widgetQuery(item, item.id ?? `w${index}`))
          .filter((query) => query !== null),
      })
      .then(({ text }) => setDisplay({ text }))
      .catch((error: unknown) =>
        setProblem(error instanceof Error ? error.message : 'The summary could not be written.'),
      )
      .finally(() => setBusy(false));
  };
  return (
    <aside className="rs" aria-label="Widget settings">
      <header className="rs-head">
        <strong>AI Summary</strong>
        <button
          type="button"
          className="rb-icon"
          aria-label="Close settings"
          onClick={props.onClose}
        >
          ×
        </button>
      </header>
      <div className="rs-body">
        <label className="rs-field">
          Title
          <input
            value={widget.display.title ?? ''}
            maxLength={120}
            onChange={(event) => setDisplay({ title: event.target.value })}
          />
        </label>
        <label className="rs-field">
          Focus on <small>(optional)</small>
          <textarea
            rows={3}
            maxLength={1000}
            value={widget.display.guidance ?? ''}
            placeholder="For example: lead with enquiries and cost per lead."
            onChange={(event) => setDisplay({ guidance: event.target.value })}
          />
        </label>
        <button type="button" className="btn" disabled={busy} onClick={generate}>
          {busy ? 'Writing…' : widget.display.text ? 'Regenerate summary' : 'Generate summary'}
        </button>
        <p className="rs-explain" aria-live="polite">
          {problem ??
            'SynAssistant reads the numbers on this page for its date range. Check and edit the text before your client sees it.'}
        </p>
        <label className="rs-field">
          Summary
          <textarea
            rows={10}
            maxLength={4000}
            value={widget.display.text ?? ''}
            onChange={(event) => setDisplay({ text: event.target.value })}
          />
        </label>
      </div>
    </aside>
  );
}
